import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { X, Trash2, Plus, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { toast } from './ui/use-toast';
import { useCompanyState } from '../hooks/useCompanyState';
import { useIsMobile } from '../hooks/use-mobile';
import { getBaseUrl } from '@/lib/api';

interface SerialNumberEntry {
  id?: string;
  partNo: string;
  spIncGST: number;
  costPrice?: number;
  batchNo: string;
  mfgDate: string;
  expiryDays: string;
  location: string;
  status: string;
}

interface AddSerialNumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRefreshProducts?: () => void;
  productCode: number;
  productName: string;
  productMRP?: number;
  productCostPrice?: number;
  token: string;
  /** When inventory "All" location is selected, omit; when a specific location is selected, pass its ID for GetSerail API */
  locationId?: string;
  /** Default for "Expiry forever" checkbox in Add Serial form. From setting "Expiry forever": Value "1" = true, "0" = false */
  defaultExpiryForever?: boolean;
}

const AddSerialNumberModal: React.FC<AddSerialNumberModalProps> = ({
  isOpen,
  onClose,
  onRefreshProducts,
  productCode,
  productName,
  productMRP,
  productCostPrice,
  token,
  locationId,
  defaultExpiryForever = true,
}) => {
  const baseUrl = getBaseUrl();
  const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

  // Helper to get today's date in DD-MM-YYYY format for display, but store as YYYY-MM-DD for input
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Returns YYYY-MM-DD for date input
  };

  const [serialNumbers, setSerialNumbers] = useState<SerialNumberEntry[]>([]);
  const [selectedSerialIds, setSelectedSerialIds] = useState<string[]>([]);
  const [showExpired, setShowExpired] = useState(false);
  const [showSold, setShowSold] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [locations, setLocations] = useState<{ ID: string; Name: string }[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSerialNumber, setNewSerialNumber] = useState<Partial<SerialNumberEntry> & { costPrice?: string }>({
    partNo: '',
    spIncGST: 0,
    batchNo: '0',
    mfgDate: getTodayDate(),
    expiryDays: '999999',
    location: '',
    costPrice: '',
  });
  const [serialNumberError, setSerialNumberError] = useState('');
  const [isForever, setIsForever] = useState(defaultExpiryForever);
  const [isAddingSerial, setIsAddingSerial] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const prevOpenRef = useRef(false);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const loadMoreStateRef = useRef({ mobilePage: 0, hasMoreMobile: true, loadingMoreMobile: false });
  const { companyState } = useCompanyState();
  const isMobile = useIsMobile();

  // Debounced search for GetSerail serialNumber param (avoid API call on every keystroke)
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Mobile: lazy-loaded list (accumulated pages)
  const MOBILE_PAGE_SIZE = 15;
  const [mobileSerialNumbers, setMobileSerialNumbers] = useState<SerialNumberEntry[]>([]);
  const [mobilePage, setMobilePage] = useState(0);
  const [hasMoreMobile, setHasMoreMobile] = useState(true);
  const [loadingMoreMobile, setLoadingMoreMobile] = useState(false);

  // Reset to first page when modal opens
  useEffect(() => {
    if (isOpen && !prevOpenRef.current) {
      setCurrentPage(0);
    }
    prevOpenRef.current = isOpen;
  }, [isOpen]);

  // Reset to first page when location, Sold filter, or search change
  useEffect(() => {
    if (isOpen) setCurrentPage(0);
  }, [isOpen, locationId, showSold, debouncedSearchQuery]);

  // Fetch locations and set product rate
  useEffect(() => {
    if (isOpen) {
      const fetchLocations = async () => {
        try {
          const response = await fetch(`${baseUrl}/api/Locations`, {
            headers: { Authorization: authToken },
          });
          const data = await response.json();
          if (data.data) {
            setLocations(data.data);
            // Set location based on company state if available, otherwise use first location
            if (data.data.length > 0 && !newSerialNumber.location) {
              const locationForCompany = data.data.find(loc => 
                loc.Name.toLowerCase().includes(companyState?.toLowerCase() || '')
              );
              const selectedLocation = locationForCompany || data.data[0];
              setNewSerialNumber(prev => ({ ...prev, location: selectedLocation.ID }));
            }
          }
        } catch (err) {
          console.error('Error fetching locations:', err);
        }
      };

      // Set the SP Incl GST (Rate) from passed product data
      // Use MRP as SP Incl GST if available, otherwise use CostPrice
      const rate = productMRP || productCostPrice || 0;
      
      // Only update if the rate is different from current value to avoid unnecessary re-renders
      if (newSerialNumber.spIncGST !== rate) {
        setNewSerialNumber(prev => ({ 
          ...prev, 
          spIncGST: rate 
        }));
      }

      fetchLocations();
    }
  }, [isOpen, baseUrl, authToken, productMRP, productCostPrice, productName, companyState]);

  // Build GetSerail query params: locationId only when set (not All), status 0=Sold 1=Available, serialNumber for search
  const getSerailParams = (pageStart: number, maxRecords: number) => {
    const params = new URLSearchParams();
    params.set('productId', String(productCode));
    params.set('pageStart', String(pageStart));
    params.set('maxRecords', String(maxRecords));
    if (locationId) params.set('locationId', locationId);
    params.set('status', showSold ? '0' : '1');
    params.set('serialNumber', debouncedSearchQuery);
    return params;
  };

  // Fetch existing serial numbers with pagination (desktop only)
  useEffect(() => {
    if (isOpen && productCode && !isMobile) {
      fetchSerialNumbers();
    }
  }, [isOpen, productCode, currentPage, pageSize, isMobile, locationId, showSold, debouncedSearchQuery]);

  // Mobile: initial load and reset when modal opens or location/status/search change
  useEffect(() => {
    if (isOpen && productCode && isMobile) {
      setMobileSerialNumbers([]);
      setMobilePage(0);
      setHasMoreMobile(true);
      fetchMobilePage(0, false);
    }
  }, [isOpen, productCode, isMobile, locationId, showSold, debouncedSearchQuery]);

  const fetchMobilePage = async (page: number, append: boolean) => {
    if (append) {
      setLoadingMoreMobile(true);
    } else {
      setIsLoading(true);
    }
    try {
      const pageStart = page * MOBILE_PAGE_SIZE;
      const params = getSerailParams(pageStart, MOBILE_PAGE_SIZE);
      const url = `${baseUrl}/api/GetSerail?${params.toString()}`;
      const response = await fetch(url, { headers: { Authorization: authToken } });
      const responseData = await response.json();
      if (responseData.status === 0 && responseData.data && Array.isArray(responseData.data)) {
        const mapped = responseData.data.map((item: any) => ({
          id: item.Id?.toString() || `api-${Date.now()}-${Math.random()}`,
          partNo: item.SerialNumber || '',
          spIncGST: item.Rate || 0,
          costPrice: typeof item.costprice === 'number' ? item.costprice : (item.CostPrice != null ? Number(item.CostPrice) : undefined),
          batchNo: item.BatchNo || '',
          mfgDate: item.MfgDate ? item.MfgDate.split('T')[0] : '',
          expiryDays: item.ExpiryDays?.toString() || '',
          location: item.LocationId?.toString() || '',
          status: item.Status === '1' ? 'Available' : item.Status === '0' ? 'Sold' : 'Unknown',
        }));
        if (append) {
          setMobileSerialNumbers((prev) => [...prev, ...mapped]);
        } else {
          setMobileSerialNumbers(mapped);
        }
        setHasMoreMobile(mapped.length === MOBILE_PAGE_SIZE);
        setMobilePage(page + 1);
      } else {
        if (!append) setMobileSerialNumbers([]);
        setHasMoreMobile(false);
      }
    } catch (error) {
      console.error('Error fetching serial numbers (mobile):', error);
      if (!append) setMobileSerialNumbers([]);
      setHasMoreMobile(false);
    } finally {
      setLoadingMoreMobile(false);
      setIsLoading(false);
    }
  };

  const fetchSerialNumbers = async () => {
    setIsLoading(true);
    try {
      const pageStart = currentPage * pageSize;
      const params = getSerailParams(pageStart, pageSize);
      const url = `${baseUrl}/api/GetSerail?${params.toString()}`;
      console.log(`=== FETCHING SERIAL NUMBERS FOR PRODUCT ${productCode} (page ${currentPage + 1}, pageSize ${pageSize}) ===`);
      
      const response = await fetch(url, {
        headers: { Authorization: authToken },
      });
      
      const responseData = await response.json();
      console.log('GET SERIAL API RESPONSE:', JSON.stringify(responseData, null, 2));
      
      if (responseData.status === 0 && responseData.data && Array.isArray(responseData.data)) {
        // Map API response to SerialNumberEntry interface
        const mappedSerialNumbers = responseData.data.map((item: any) => ({
          id: item.Id?.toString() || `api-${Date.now()}-${Math.random()}`,
          partNo: item.SerialNumber || '',
          spIncGST: item.Rate || 0,
          costPrice: typeof item.costprice === 'number' ? item.costprice : (item.CostPrice != null ? Number(item.CostPrice) : undefined),
          batchNo: item.BatchNo || '',
          mfgDate: item.MfgDate ? item.MfgDate.split('T')[0] : '',
          expiryDays: item.ExpiryDays?.toString() || '',
          location: item.LocationId?.toString() || '',
          status: item.Status === '1' ? 'Available' : item.Status === '0' ? 'Sold' : 'Unknown',
        }));
        
        setSerialNumbers(mappedSerialNumbers);
        // Support optional totalCount from API (e.g. responseData.totalCount or responseData.total)
        if (typeof responseData.totalCount === 'number') {
          setTotalCount(responseData.totalCount);
        } else if (typeof responseData.total === 'number') {
          setTotalCount(responseData.total);
        } else {
          setTotalCount(null);
        }
        console.log(`Successfully mapped ${mappedSerialNumbers.length} serial numbers`);
      } else if (responseData.status === -1) {
        console.log('No serial numbers found or error:', responseData.message);
        setSerialNumbers([]);
        setTotalCount(0);
      } else {
        console.log('Unexpected response format:', responseData);
        setSerialNumbers([]);
        setTotalCount(null);
      }
    } catch (error) {
      console.error('Error fetching serial numbers:', error);
      setSerialNumbers([]);
      setTotalCount(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = () => {
    if (selectedSerialIds.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select serial numbers to delete',
        variant: 'destructive',
      });
      return;
    }
    
    // Show confirmation popup
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      // Extract the numeric IDs from the selected serial IDs
      const serialIdsToDelete = selectedSerialIds.map(id => {
        // Handle both API IDs (like "26") and temp IDs (like "temp-...")
        if (id.startsWith('api-')) {
          return parseInt(id.replace('api-', '').split('-')[0]);
        } else if (!isNaN(parseInt(id))) {
          return parseInt(id);
        }
        return null;
      }).filter(id => id !== null);

      const payload = {
        Id: serialIdsToDelete
      };

      console.log('=== DELETE INVENTORY API REQUEST ===');
      console.log('URL:', `${baseUrl}/api/DeleteInventory`);
      console.log('Method:', 'Delete');
      console.log('Headers:', {
        'Content-Type': 'application/json',
        'Authorization': authToken
      });
      console.log('Request Payload:', JSON.stringify(payload, null, 2));

      const response = await fetch(`${baseUrl}/api/DeleteInventory`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();
      console.log('=== DELETE INVENTORY API RESPONSE ===');
      console.log('Status:', response.status);
      console.log('Response Data:', JSON.stringify(responseData, null, 2));

      if (responseData.status === -1) {
        toast({
          title: 'Delete Failed',
          description: responseData.message || 'Failed to delete serial numbers',
          variant: 'destructive',
        });
      } else if (response.ok) {
        toast({
          title: 'Success',
          description: `${selectedSerialIds.length} serial number(s) deleted successfully`,
        });
        setSelectedSerialIds([]);
        await refreshSerialList();
      } else {
        toast({
          title: 'Delete Failed',
          description: responseData.message || 'Failed to delete serial numbers',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting serial numbers:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete serial numbers',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleSave = () => {
    // Call refresh callback if provided, then close the modal
    if (onRefreshProducts) {
      onRefreshProducts();
    }
    onClose();
  };

  const toggleSerialSelection = (id: string) => {
    setSelectedSerialIds(prev =>
      prev.includes(id)
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    );
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    try {
      // Handle YYYY-MM-DD format (from API)
      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts[0].length === 4) {
          // YYYY-MM-DD format - extract date parts directly to avoid timezone issues
          const cleanDate = dateStr.split('T')[0]; // Remove time part if present
          const [year, month, day] = cleanDate.split('-');
          return `${day}-${month}-${year}`;
        } else {
          // DD-MM-YYYY format
          return dateStr;
        }
      } else {
        return dateStr;
      }
    } catch {
      return dateStr;
    }
  };

  const displayList = isMobile ? mobileSerialNumbers : serialNumbers;
  const filteredSerialNumbers = displayList.filter(serial => {
    // Apply search filter first
    if (searchQuery.trim()) {
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        serial.partNo.toLowerCase().includes(searchLower) ||
        serial.batchNo.toLowerCase().includes(searchLower) ||
        serial.location.toLowerCase().includes(searchLower) ||
        serial.status.toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;
    }
    
    // Default: show Available (status = 'Available' which comes from API status = '1')
    if (!showExpired && !showSold) {
      return serial.status === 'Available';
    }
    
    // Show Expired items
    if (showExpired && serial.status !== 'Expired') {
      return false;
    }
    
    // Show Sold items (when Sold checkbox is checked) - show products with status 0 as sold
    if (showSold && serial.status !== 'Sold') {
      return false;
    }
    
    // If only Sold checkbox is checked, show only Sold items (status 0)
    if (showSold && !showExpired) {
      return serial.status === 'Sold';
    }
    
    // If only Expired checkbox is checked, show only Expired items
    if (showExpired && !showSold) {
      return serial.status === 'Expired';
    }
    
    // If both checkboxes are checked, show both Sold and Expired
    if (showExpired && showSold) {
      return serial.status === 'Sold' || serial.status === 'Expired';
    }
    
    return true;
  });

  const getLocationName = (locationId: string) => {
    const location = locations.find(loc => loc.ID === locationId);
    return location ? location.Name : locationId;
  };

  // Handlers for the new serial number form
  const handleOpenAddForm = () => {
    setShowAddForm(true);
    setSerialNumberError('');
    // Reset form with current product rate and company-based location
    const locationForCompany = locations.find(loc => 
      loc.Name.toLowerCase().includes(companyState?.toLowerCase() || '')
    );
    const selectedLocation = locationForCompany || (locations.length > 0 ? locations[0] : null);
    const forever = defaultExpiryForever;
    
    setNewSerialNumber({
      partNo: '',
      spIncGST: newSerialNumber.spIncGST || 0,
      batchNo: '0',
      mfgDate: getTodayDate(),
      expiryDays: forever ? '999999' : '0',
      location: selectedLocation?.ID || '',
      costPrice: '',
    });
    setIsForever(forever);
  };

  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setSerialNumberError('');
    setIsAddingSerial(false);
  };

  const handleAddSerialSubmit = async () => {
    // Validation
    if (!newSerialNumber.partNo?.trim()) {
      setSerialNumberError('Part No is required');
      return;
    }

    if (!newSerialNumber.location) {
      setSerialNumberError('Location is required');
      return;
    }

    setIsAddingSerial(true);
    setSerialNumberError('');

    try {
      // First check if serial number exists
      console.log('=== CHECK SERIAL EXISTS API REQUEST ===');
      console.log('URL:', `${baseUrl}/api/CheckSerialExists`);
      console.log('Method:', 'POST');
      console.log('Headers:', {
        'Content-Type': 'application/json',
        'Authorization': authToken
      });
      console.log('Request Payload:', JSON.stringify({
        productId: productCode,
        serialNumber: newSerialNumber.partNo.trim()
      }, null, 2));

      // Add debug info
      console.log('DEBUG: Product Code:', productCode);
      console.log('DEBUG: Serial Number:', newSerialNumber.partNo.trim());
      console.log('DEBUG: Base URL:', baseUrl);
      console.log('DEBUG: Auth Token:', authToken.substring(0, 20) + '...');

      const checkResponse = await fetch(`${baseUrl}/api/CheckSerialExists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
        body: JSON.stringify({
          productId: productCode,
          serialNumber: newSerialNumber.partNo.trim()
        }),
      });

      console.log('DEBUG: Check Response Status:', checkResponse.status);
      console.log('DEBUG: Check Response OK:', checkResponse.ok);

      const checkData = await checkResponse.json();
      console.log('=== CHECK SERIAL EXISTS API RESPONSE ===');
      console.log('Status:', checkResponse.status);
      console.log('Response Data:', JSON.stringify(checkData, null, 2));

      // Handle response based on status
      console.log('DEBUG: Handling response with status:', checkData.status);
      
      if (checkData.status === 0) {
        console.log('DEBUG: Status is 0 - Serial number available, proceeding to add');
        // Build payload to match api/CreateSerial JSON: ProductId, SerialNumber, Rate, MfgDate, ExpiryDays, LocationId, BatchNo, CostPrice
        const mfgDateVal = newSerialNumber.mfgDate || new Date().toISOString().split('T')[0];
        const mfgDateIso = mfgDateVal.includes('T') ? mfgDateVal : `${mfgDateVal}T00:00:00`;
        const parsedExpiry = parseInt(newSerialNumber.expiryDays || '0', 10);
        const payload = {
          ProductId: productCode,
          SerialNumber: newSerialNumber.partNo.trim(),
          Rate: Number(newSerialNumber.spIncGST) || 0,
          MfgDate: mfgDateIso,
          ExpiryDays: isForever ? 999999 : (isNaN(parsedExpiry) ? 0 : parsedExpiry),
          LocationId: parseInt(newSerialNumber.location || '0', 10),
          BatchNo: newSerialNumber.batchNo || '',
          CostPrice: (newSerialNumber.costPrice !== undefined && newSerialNumber.costPrice !== '')
            ? String(newSerialNumber.costPrice)
            : (productCostPrice != null ? String(productCostPrice) : '0'),
        };
        const payloadArray = [payload];

        console.log('=== ADD NEW SERIAL API REQUEST ===');
        console.log('URL:', `${baseUrl}/api/CreateSerial`);
        console.log('Method:', 'POST');
        console.log('Headers:', {
          'Content-Type': 'application/json',
          'Authorization': authToken
        });
        console.log('Request Payload:', JSON.stringify(payloadArray, null, 2));

        const response = await fetch(`${baseUrl}/api/CreateSerial`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
          },
          body: JSON.stringify(payloadArray),
        });

        const responseData = await response.json();
        console.log('=== ADD NEW SERIAL API RESPONSE ===');
        console.log('Status:', response.status);
        console.log('Response Data:', JSON.stringify(responseData, null, 2));

        if (responseData.status === -1) {
          // Serial already exists (double check)
          setSerialNumberError(responseData.message || 'This serial number already exists');
          setIsAddingSerial(false);
        } else if (response.ok) {
          // Success
          toast({
            title: 'Success',
            description: 'Serial number added successfully',
          });
          
          // Reset form for next entry but keep the form open
          setNewSerialNumber({
            partNo: '',
            spIncGST: newSerialNumber.spIncGST || 0,
            batchNo: '0',
            mfgDate: getTodayDate(),
            expiryDays: defaultExpiryForever ? '999999' : '0',
            location: newSerialNumber.location, // Keep the same location
            costPrice: '',
          });
          setSerialNumberError('');
          setIsAddingSerial(false);
          setIsForever(defaultExpiryForever);
          await refreshSerialList();
          
        } else {
          setSerialNumberError(responseData.message || 'Failed to add serial number');
          setIsAddingSerial(false);
        }
      } else if (checkData.status === -1 || checkData.status === 1) {
        console.log('DEBUG: Status is -1 or 1 - Serial number exists or error, showing error');
        // Serial number exists or error, show message and don't add
        setSerialNumberError(checkData.message || 'Serial number already exists or error occurred');
        setIsAddingSerial(false);
        return;
      } else {
        console.log('DEBUG: Unexpected status:', checkData.status);
        // Unexpected status
        setSerialNumberError('Unexpected error occurred while checking serial number');
        setIsAddingSerial(false);
        return;
      }
    } catch (error) {
      console.error('Error adding serial number:', error);
      setSerialNumberError('Failed to add serial number. Please try again.');
      setIsAddingSerial(false);
    }
  };

  // Refresh list after add/delete (desktop: refetch page; mobile: reset and load first page)
  const refreshSerialList = async () => {
    if (isMobile) {
      setMobileSerialNumbers([]);
      setMobilePage(0);
      setHasMoreMobile(true);
      await fetchMobilePage(0, false);
    } else {
      await fetchSerialNumbers();
    }
  };

  loadMoreStateRef.current = { mobilePage, hasMoreMobile, loadingMoreMobile };

  // Mobile: lazy load more when sentinel is visible
  useEffect(() => {
    if (!isOpen || !isMobile) return;
    const el = loadMoreSentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        const { mobilePage: p, hasMoreMobile: more, loadingMoreMobile: loading } = loadMoreStateRef.current;
        if (more && !loading) fetchMobilePage(p, true);
      },
      { root: null, rootMargin: '100px', threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isOpen, isMobile]);

  // Wrapper function to handle modal close with refresh
  const handleClose = () => {
    if (onRefreshProducts) {
      onRefreshProducts();
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-0 w-[95vw] sm:w-full">
        {/* Header */}
        <div className="flex items-center justify-center p-4 border-b bg-gray-50">
          <DialogTitle className="text-xl font-semibold">Add Serial Number</DialogTitle>
        </div>

        {/* Product Name */}
        <div className="px-4 pt-2 pb-1">
          <p className="text-sm font-medium text-gray-700">{productName}</p>
        </div>

        {/* Header Actions - Add Button and Search */}
        <div className="px-4 py-4 border-b bg-gray-50">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
            {/* Search Box */}
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-4 w-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search serial numbers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500"
              />
            </div>
            
            {/* Add Button */}
            <Button
              onClick={handleOpenAddForm}
              className="bg-teal-500 hover:bg-teal-600 text-white w-full sm:w-auto px-6 py-2"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Serial
            </Button>
          </div>
        </div>

        {/* Add Serial Number Form - New Form */}
        {showAddForm && (
          <div className="px-4 py-4 border-b bg-blue-50">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-blue-900">Add New Serial Number</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloseAddForm}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Error Message */}
            {serialNumberError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md">
                <p className="text-sm text-red-700">{serialNumberError}</p>
              </div>
            )}

            <div className="flex flex-col lg:flex-row gap-4 lg:gap-6">
              <form onSubmit={(e) => e.preventDefault()} className="flex flex-col lg:flex-row gap-4 lg:gap-6 w-full">
                <div className="space-y-2 flex-1 min-w-[150px]">
                  <Label htmlFor="newPartNo" className="text-sm font-medium text-blue-900">
                    PartNO *
                  </Label>
                  <Input
                    id="newPartNo"
                    value={newSerialNumber.partNo || ''}
                    onChange={(e) => {
                      setNewSerialNumber({ ...newSerialNumber, partNo: e.target.value });
                      setSerialNumberError(''); // Clear error on input
                    }}
                    placeholder="Enter Part No"
                    className={`w-full ${serialNumberError && !newSerialNumber.partNo?.trim() ? 'border-red-300 focus:border-red-500' : ''}`}
                    disabled={isAddingSerial}
                  />
                </div>

                <div className="space-y-2 flex-1 min-w-[120px]">
                  <Label htmlFor="newSpIncGST" className="text-sm font-medium text-blue-900">
                    SP Incl GST
                  </Label>
                  <Input
                    key="spIncGST-input"
                    id="newSpIncGST"
                    type="number"
                    step="0.01"
                    value={newSerialNumber.spIncGST || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only update state if the value is valid or empty
                      if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                        setNewSerialNumber(prev => ({
                          ...prev,
                          spIncGST: value === '' ? 0 : parseFloat(value)
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure proper formatting when user leaves the field
                      const value = parseFloat(e.target.value) || 0;
                      setNewSerialNumber(prev => ({
                        ...prev,
                        spIncGST: value
                      }));
                    }}
                    placeholder="0.00"
                    className="w-full"
                    disabled={isAddingSerial}
                  />
                  {newSerialNumber.spIncGST > 0 && (
                    <p className="text-xs text-green-600">Rate set: ₹{newSerialNumber.spIncGST.toFixed(2)}</p>
                  )}
                </div>

                <div className="space-y-2 flex-1 min-w-[120px]">
                  <Label htmlFor="newBatchNo" className="text-sm font-medium text-blue-900">
                    Batch No
                  </Label>
                  <Input
                    id="newBatchNo"
                    value={newSerialNumber.batchNo || ''}
                    onChange={(e) =>
                      setNewSerialNumber({ ...newSerialNumber, batchNo: e.target.value })
                    }
                    placeholder="Enter Batch No"
                    className="w-full"
                    disabled={isAddingSerial}
                  />
                </div>

                <div className="space-y-2 flex-1 min-w-[140px]">
                  <Label htmlFor="newMfgDate" className="text-sm font-medium text-blue-900">
                    MFG Date
                  </Label>
                  <Input
                    id="newMfgDate"
                    type="date"
                    value={newSerialNumber.mfgDate}
                    onChange={(e) =>
                      setNewSerialNumber({ ...newSerialNumber, mfgDate: e.target.value })
                    }
                    className="w-full"
                    disabled={isAddingSerial}
                  />
                </div>

                <div className="space-y-2 flex-1 min-w-[140px]">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="newExpiryDays" className="text-sm font-medium text-blue-900">
                      Expiry Days
                    </Label>
                    <div className="flex items-center space-x-1">
                      <Checkbox
                        id="newForever"
                        checked={isForever}
                        onCheckedChange={(checked) => {
                          setIsForever(!!checked);
                          if (checked) {
                            setNewSerialNumber({ ...newSerialNumber, expiryDays: '999999' });
                          } else {
                            setNewSerialNumber({ ...newSerialNumber, expiryDays: '0' });
                          }
                        }}
                        disabled={isAddingSerial}
                        className="w-3 h-3"
                      />
                      <Label htmlFor="newForever" className="text-xs cursor-pointer text-blue-900">
                        Forever
                      </Label>
                    </div>
                  </div>
                  <Input
                    id="newExpiryDays"
                    type="number"
                    value={newSerialNumber.expiryDays}
                    onChange={(e) =>
                      setNewSerialNumber({ ...newSerialNumber, expiryDays: e.target.value })
                    }
                    placeholder="Enter days"
                    disabled={isForever || isAddingSerial}
                    readOnly={isForever}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2 flex-1 min-w-[150px]">
                  <Label htmlFor="newLocation" className="text-sm font-medium text-blue-900">
                    Location *
                  </Label>
                  <select
                    id="newLocation"
                    value={newSerialNumber.location}
                    onChange={(e) =>
                      setNewSerialNumber({ ...newSerialNumber, location: e.target.value })
                    }
                    className={`w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${serialNumberError && !newSerialNumber.location ? 'border-red-300 focus:border-red-500' : ''}`}
                    disabled={isAddingSerial}
                  >
                    <option value="">Select Location</option>
                    {locations.map((loc) => (
                      <option key={loc.ID} value={loc.ID}>
                        {loc.Name}
                      </option>
                    ))}
                  </select>
                </div>
              </form>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="outline"
                onClick={handleCloseAddForm}
                disabled={isAddingSerial}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddSerialSubmit}
                disabled={isAddingSerial}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isAddingSerial ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Serial'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Table (desktop) / Cards (mobile) Section */}
        <div className="px-2 sm:px-4 py-4 overflow-x-auto">
          {isLoading && !loadingMoreMobile ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : filteredSerialNumbers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No serial numbers added yet
            </div>
          ) : isMobile ? (
            /* Mobile: card list with lazy loading */
            <div className="space-y-3 pb-4">
              {filteredSerialNumbers.map((serial) => {
                const isSelected = selectedSerialIds.includes(serial.id || '');
                return (
                  <div
                    key={serial.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleSerialSelection(serial.id || '')}
                    onKeyDown={(e) => e.key === 'Enter' && toggleSerialSelection(serial.id || '')}
                    className={`rounded-lg border p-4 shadow-sm transition-colors ${
                      isSelected ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSerialSelection(serial.id || '')}
                          className="h-4 w-4 shrink-0 cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className={`font-medium truncate ${isSelected ? 'text-teal-700' : 'text-gray-900'}`}>
                          {serial.partNo}
                        </span>
                        <span
                          className={`shrink-0 px-2 py-0.5 rounded text-xs ${
                            serial.status === 'Available'
                              ? 'bg-green-100 text-green-700'
                              : serial.status === 'Sold'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {serial.status}
                        </span>
                      </div>
                        <span className="text-sm font-medium text-gray-700">₹{serial.spIncGST.toFixed(2)}</span>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-gray-600">
                      <span>Batch: {serial.batchNo || 'N/A'}</span>
                      <span>Mfg: {formatDate(serial.mfgDate)}</span>
                      <span>Expiry: {serial.expiryDays === '999999' ? 'Forever' : serial.expiryDays || 'N/A'}</span>
                      <span>Location: {getLocationName(serial.location)}</span>
                    </div>
                  </div>
                );
              })}
              <div ref={loadMoreSentinelRef} className="h-4 flex items-center justify-center py-4">
                {loadingMoreMobile && (
                  <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
                )}
              </div>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12 sticky left-0 bg-white z-10"></TableHead>
                      <TableHead className="min-w-[120px]">PartNO</TableHead>
                      <TableHead className="min-w-[100px]">SP Inc GST</TableHead>
                      <TableHead className="min-w-[100px]">BatchNo</TableHead>
                      <TableHead className="min-w-[100px]">MfgDate</TableHead>
                      <TableHead className="min-w-[100px]">ExpiryDays</TableHead>
                      <TableHead className="min-w-[120px]">Location</TableHead>
                      <TableHead className="min-w-[100px]">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSerialNumbers.map((serial) => {
                      const isSelected = selectedSerialIds.includes(serial.id || '');
                      return (
                        <TableRow
                          key={serial.id}
                          className={`cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-blue-100 border-blue-300'
                              : 'hover:bg-gray-50'
                          }`}
                          onClick={() => toggleSerialSelection(serial.id || '')}
                        >
                          <TableCell className="sticky left-0 bg-inherit z-10" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSerialSelection(serial.id || '')}
                              className="h-4 w-4 cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell className={`font-medium ${isSelected ? 'text-blue-700' : ''}`}>
                            {serial.partNo}
                          </TableCell>
                          <TableCell>₹{serial.spIncGST.toFixed(2)}</TableCell>
                          <TableCell>{serial.batchNo || 'N/A'}</TableCell>
                          <TableCell>{formatDate(serial.mfgDate)}</TableCell>
                          <TableCell>
                            {serial.expiryDays === '999999' ? 'Forever' : serial.expiryDays || 'N/A'}
                          </TableCell>
                          <TableCell>{getLocationName(serial.location)}</TableCell>
                          <TableCell>
                            <span
                              className={`px-2 py-1 rounded text-xs ${
                                serial.status === 'Available'
                                  ? 'bg-green-100 text-green-700'
                                  : serial.status === 'Sold'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {serial.status}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Pagination (desktop only) */}
          {!isMobile && !isLoading && serialNumbers.length >= 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 px-2 sm:px-4 py-3 border-t bg-gray-50/80">
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">Rows per page</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    const newSize = Number(e.target.value);
                    setPageSize(newSize);
                    setCurrentPage(0);
                  }}
                  className="h-9 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-600">
                  {totalCount != null
                    ? `Page ${currentPage + 1} of ${Math.max(1, Math.ceil(totalCount / pageSize))}`
                    : `Page ${currentPage + 1}`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                  disabled={currentPage === 0}
                  className="h-9 gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => p + 1)}
                  disabled={
                    totalCount != null
                      ? (currentPage + 1) * pageSize >= totalCount
                      : serialNumbers.length < pageSize
                  }
                  className="h-9 gap-1"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 border-t bg-gray-50">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            {selectedSerialIds.length > 0 && (
            <Button
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-500 hover:bg-red-600 text-white w-full sm:w-auto"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete ({selectedSerialIds.length})
            </Button>
          )}
            <div className="flex items-center gap-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="expired"
                  checked={showExpired}
                  onCheckedChange={(checked) => setShowExpired(!!checked)}
                />
                <Label htmlFor="expired" className="text-sm cursor-pointer">
                  Expired
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sold"
                  checked={showSold}
                  onCheckedChange={(checked) => setShowSold(!!checked)}
                />
                <Label htmlFor="sold" className="text-sm cursor-pointer">
                  Sold
                </Label>
              </div>
            </div>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              onClick={handleClose}
              className="bg-teal-500 hover:bg-teal-600 text-white flex-1 sm:flex-initial"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md w-[90vw] p-0">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Deletion
              </h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the selected Serial Number(s)?
            </p>
            
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={cancelDelete}
                disabled={isDeleting}
                className="border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default AddSerialNumberModal;
